import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, TrendingUp } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProgressRing } from "../components/ProgressRing";
import { MilestoneBadge } from "../components/MilestoneBadge";

function tierFor(score) {
    const s = parseFloat(score || 0);
    if (s >= 90) return "gold";
    if (s >= 75) return "silver";
    if (s >= 60) return "bronze";
    return "none";
}

export default function KraPage() {
    const { user } = useAuth();
    const [card, setCard] = useState(null);
    const [leaderboard, setLeaderboard] = useState(null);
    const [weights, setWeights] = useState(null);
    const [savingWeights, setSavingWeights] = useState(false);
    const canEditWeights = user?.effective_role === "OWNER" || user?.effective_role === "HEAD";
    const seesLeaderboard = canEditWeights;

    useEffect(() => {
        api.get("/kra/me").then(({ data }) => setCard(data)).catch((e) => toast.error(apiError(e)));
        api.get("/kra/weights").then(({ data }) => setWeights(data)).catch(() => {});
        if (user?.effective_role === "OWNER" || user?.effective_role === "HEAD") {
            api.get("/kra/leaderboard").then(({ data }) => setLeaderboard(data)).catch(() => {});
        }
    }, [user?.effective_role]);

    async function saveWeights() {
        const total = (weights.task || 0) + (weights.problem || 0) + (weights.reviews || 0) + (weights.discipline || 0);
        if (total !== 100) { toast.error(`Weights must sum to 100 (got ${total})`); return; }
        setSavingWeights(true);
        try {
            await api.patch("/kra/weights", {
                task: parseInt(weights.task), problem: parseInt(weights.problem),
                reviews: parseInt(weights.reviews), discipline: parseInt(weights.discipline),
            });
            toast.success("Weights saved");
            const { data } = await api.get("/kra/me"); setCard(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSavingWeights(false); }
    }

    if (!card) return <div className="mx-auto max-w-5xl p-6"><div className="h-64 rounded-xl bg-white animate-pulse" /></div>;
    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8" data-testid="kra-page">
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">KRA · scorecard</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                    <Award className="w-6 h-6" /> {card.user_name || card.user_email} · {card.month}
                </h1>
                <div className="text-xs text-gray-500 mt-1">Role: {card.effective_role} · {card.frozen ? "Frozen snapshot (finalised)" : "Live"}</div>
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-gray-100 fx-shadow-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-5">
                        <ProgressRing
                            value={parseFloat(card.overall) || 0}
                            size={104}
                            strokeWidth={9}
                            accent="#1E3355"
                            label={String(card.overall)}
                            sublabel="score"
                            testid="overall-ring"
                        />
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-gray-500">Overall</div>
                            <div className="font-display text-2xl font-bold text-[#1E3355]" data-testid="overall-score">{card.overall}</div>
                            <div className="mt-2"><MilestoneBadge tier={tierFor(card.overall)} testid="overall-badge" /></div>
                        </div>
                    </div>
                    <div className="text-xs text-gray-500">
                        Weights: task {card.weights.task} · problem {card.weights.problem} · reviews {card.weights.reviews} · discipline {card.weights.discipline}
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {card.components.map((c) => (
                        <div key={c.key} className="rounded-xl border border-gray-100 p-3 flex gap-3 items-start bg-gradient-to-br from-white to-gray-50" data-testid={`kra-component-${c.key}`}>
                            <ProgressRing value={parseFloat(c.score) || 0} size={52} strokeWidth={5} accent="#1E3355" label={c.score == null ? "—" : String(c.score)} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[#1E3355]">{c.label}</div>
                                <ul className="mt-1 space-y-0.5 text-[11px] text-gray-600">
                                    {(c.drivers || []).map((d, i) => <li key={i}>· {d}</li>)}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {canEditWeights && weights && (
                <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5" data-testid="weights-editor">
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-widest mb-3">Weight editor (Owner/Head)</h3>
                    <div className="grid grid-cols-4 gap-3">
                        {["task", "problem", "reviews", "discipline"].map((k) => (
                            <label key={k} className="text-xs">
                                <span className="capitalize block text-gray-500">{k}</span>
                                <input type="number" min="0" max="100" value={weights[k] ?? ""} onChange={(e) => setWeights({ ...weights, [k]: parseInt(e.target.value || "0") })} className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm bg-white" data-testid={`weight-${k}`} />
                            </label>
                        ))}
                    </div>
                    <button onClick={saveWeights} disabled={savingWeights} className="mt-3 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-1.5 rounded-md" data-testid="save-weights">
                        {savingWeights ? "Saving…" : "Save weights"}
                    </button>
                </div>
            )}

            {seesLeaderboard && leaderboard && (
                <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5" data-testid="leaderboard">
                    <h3 className="font-display font-bold text-sm text-[#1E3355] uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4" /> Team leaderboard · {leaderboard.month}
                    </h3>
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F6F9]">
                            <tr><th className="text-left p-2">User</th><th className="text-left p-2">Role</th><th className="text-right p-2">Overall</th></tr>
                        </thead>
                        <tbody>
                            {leaderboard.rows.map((r, i) => (
                                <tr key={r.user_id} className={`border-t border-gray-100 ${i < 3 ? "bg-emerald-50/40" : i >= leaderboard.rows.length - 3 ? "bg-red-50/40" : ""}`}>
                                    <td className="p-2 font-medium">
                                        <div className="flex items-center gap-2">
                                            <span>{r.user_name}</span>
                                            <MilestoneBadge tier={tierFor(r.overall)} size="sm" testid={`badge-lb-${i}`} />
                                        </div>
                                    </td>
                                    <td className="p-2 text-xs text-gray-500">{r.effective_role}</td>
                                    <td className="p-2 text-right font-semibold text-[#1E3355]">{r.overall}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
